import { getImageUrl, getProductImageUrl } from '../services/imageService';
import { bannerHeroActions, campaignHeroActions } from './heroActions';

// Stands in for the product shot until an admin publishes something.
const PLACEHOLDER_PRODUCT = `${process.env.PUBLIC_URL}/hero-watch.png`;

const FALLBACK_HEADING = 'Timeless Style, Redefined';
const FALLBACK_SUBTITLE = 'Premium Watches for Every Occasion';

// Long enough for the two-line tagline the hero is built around, short enough
// that a full product description cannot push the buttons off the stage.
const TAGLINE_MAX = 140;

const ENTITIES = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};

/**
 * The opening line of a product description, fit for the hero.
 *
 * Descriptions are authored as HTML and run to whole paragraphs, so the tags
 * come out, the whitespace collapses, and only the first sentence survives.
 */
export const heroTagline = description => {
  const text = String(description || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&amp;|&lt;|&gt;|&quot;|&#39;|&nbsp;/g, entity => ENTITIES[entity])
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return '';

  const sentence = text.match(/^.*?[.!?](?=\s|$)/)?.[0] || text;
  if (sentence.length <= TAGLINE_MAX) return sentence;

  const clipped = sentence.slice(0, TAGLINE_MAX);
  const lastSpace = clipped.lastIndexOf(' ');
  return `${(lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).trim()}…`;
};

const campaignSlide = campaign => {
  const { primary, secondary } = campaignHeroActions(campaign);

  return {
    id: `campaign-${campaign.id}`,
    kicker: campaign.kicker || null,
    heading: campaign.title,
    subtitle: campaign.subtitle || '',
    image: campaign.image_url ? getImageUrl(campaign.image_url) : null,
    mobileImage: campaign.mobile_image_url
      ? getImageUrl(campaign.mobile_image_url)
      : null,
    primary,
    secondary,
  };
};

/**
 * A hero row is a product plus optional copy that overrides what the product
 * itself says, so the same watch can be pitched differently in the hero than
 * it is on its own page.
 */
const productSlide = row => {
  const product = row?.product;
  if (!product) return null;

  return {
    id: `product-${row.id}`,
    kicker: row.kicker || product.category?.name || null,
    heading: row.title || product.name,
    subtitle: row.description || heroTagline(product.description),
    image: getProductImageUrl(product.images),
    mobileImage: null,
    primary: {
      label: row.button_text || 'Shop Now',
      href: `/products/${product.slug}`,
    },
    secondary: { label: 'View More', href: '/products' },
  };
};

const bannerSlide = banner => {
  const { primary, secondary } = bannerHeroActions(banner);

  return {
    id: 'banner',
    // hero_banners has no brand line of its own.
    kicker: null,
    heading: banner?.heading || FALLBACK_HEADING,
    subtitle: banner?.subtitle || FALLBACK_SUBTITLE,
    image: banner?.image_url
      ? getImageUrl(banner.image_url)
      : PLACEHOLDER_PRODUCT,
    mobileImage: banner?.mobile_image_url
      ? getImageUrl(banner.mobile_image_url)
      : null,
    primary,
    secondary,
  };
};

/**
 * Everything the hero has to show, in the order it is shown.
 *
 * Scheduled campaigns lead, the hand-picked products follow, and the single
 * hero_banners slide covers the case where a store has published neither -
 * so the slot is never empty.
 */
export const buildHeroSlides = ({
  campaigns = [],
  heroProducts = [],
  banner = null,
} = {}) => {
  const slides = [
    ...campaigns.map(campaignSlide),
    ...heroProducts.map(productSlide),
  ].filter(Boolean);

  return slides.length > 0 ? slides : [bannerSlide(banner)];
};
